import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanLine, QrCode, CreditCard, Keyboard, Camera, LogIn, LogOut, WifiOff, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Panel, Field, Sel, BranchSel, StatusBadge } from "@/components/kit";
import { FeeLines, PayDialog } from "@/components/Payment";
import OverrideDialog from "@/components/OverrideDialog";
import { api, fmtErr, peso, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enqueue, isOffline, isForcedOffline, setForcedOffline } from "@/lib/offline";

const METHODS = [["lpr", "LPR", ScanLine], ["qr", "QR", QrCode], ["rfid", "RFID", CreditCard], ["manual", "Manual", Keyboard]];
const L = "ABCDEFGHJKLMNPRSTUVWXYZ";
const randPlate = () => Array.from({ length: 3 }, () => L[Math.floor(Math.random() * L.length)]).join("") + " " + (1000 + Math.floor(Math.random() * 9000));

function Result({ r, onPay, onOverride }) {
  if (!r) return <div className="flex h-full min-h-[160px] items-center justify-center text-sm text-muted-foreground">Awaiting vehicle…</div>;
  const ok = r.allowed;
  return (
    <div className={`space-y-3 rounded-lg border-2 p-4 fade-up ${r.queued ? "border-amber-500 bg-amber-500/5" : ok ? "border-emerald-500 bg-emerald-500/5" : "border-red-500 bg-red-500/5"}`} data-testid="lane-result">
      <div className="flex items-center gap-2 text-lg font-extrabold">
        {r.queued ? <WifiOff className="h-5 w-5 text-amber-500" /> : ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
        <span data-testid="lane-result-title">{r.queued ? "QUEUED OFFLINE" : ok ? "GATE OPEN — PROCEED" : `DENIED: ${label(r.reason)}`}</span>
      </div>
      {r.message && <div className="text-sm">{r.message}</div>}
      {r.session && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="plate">{r.session.plate}</span>
          <span className="font-mono text-xs">{r.session.session_no}</span>
          {r.session.ticket_code && <span className="font-mono text-xs">Ticket <b data-testid="lane-ticket-code">{r.session.ticket_code}</b></span>}
          {r.slot && <span className="text-xs">Slot <b>{r.slot}</b></span>}
          <StatusBadge status={r.session.status} />
        </div>
      )}
      {r.queued && <div className="font-mono text-xs">Temporary local ref: <b data-testid="lane-offline-ref">{r.queued}</b></div>}
      {r.watchlist && <div className="rounded bg-rose-600/20 px-2 py-1 text-xs font-bold text-rose-400">WATCHLIST: {label(r.watchlist.category)} — {r.watchlist.reason}</div>}
      {r.barrier && <div className="font-mono text-xs text-muted-foreground">Barrier {r.barrier.device_code}: {r.barrier.action} → {r.barrier.response}</div>}
      {r.fee && <FeeLines fee={r.fee} />}
      <div className="flex flex-wrap gap-2">
        {r.reason === "UNPAID" && <Button data-testid="lane-pay-btn" className="bg-emerald-600 hover:bg-emerald-500" onClick={onPay}>Collect {peso(r.fee?.balance)}</Button>}
        {["BARRIER_FAILED", "UNPAID", "NO_SESSION", "NOT_RECOGNIZED"].includes(r.reason) && <Button variant="outline" data-testid="lane-override-btn" onClick={onOverride}>Manual override…</Button>}
      </div>
    </div>
  );
}

function LanePanel({ dir, branchId, lanes }) {
  const { can } = useAuth();
  const [laneId, setLaneId] = useState("");
  const [method, setMethod] = useState("lpr");
  const [f, setF] = useState({ plate: "", vehicle_type: "car", qr_code: "", rfid: "", ticket_code: "", lost_ticket: false });
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const [pay, setPay] = useState(false);
  const [ovr, setOvr] = useState(false);
  const my = lanes.filter((l) => l.direction === dir);
  useEffect(() => { setLaneId(my[0]?.id || ""); setR(null); }, [branchId, lanes.length]); // eslint-disable-line
  const lane = my.find((l) => l.id === laneId);
  const submit = async () => {
    setBusy(true);
    const payload = { branch_id: branchId, lane_id: laneId, method, ...f, plate: f.plate || null };
    try {
      if (isOffline()) {
        const it = enqueue(dir, payload);
        setR({ allowed: true, queued: it.client_ref, message: `No connection. ${dir === "entry" ? "Temporary local session created" : "Exit recorded locally"}; barrier opened by local controller. Will sync & dedupe automatically.` });
        toast.warning(`OFFLINE ${dir} queued: ${it.client_ref}`);
        return;
      }
      const { data } = await api.post(`/${dir}`, payload);
      setR(data);
      data.allowed ? toast.success(`${label(dir)} OK — ${data.session?.plate}`) : toast.error(`${label(data.reason)}: ${data.message}`);
      if (data.watchlist) toast.warning(`Watchlist: ${data.watchlist.plate} (${data.watchlist.category})`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };
  const recheck = async () => {
    setPay(false);
    setTimeout(submit, 200);
  };
  const Icon = dir === "entry" ? LogIn : LogOut;
  return (
    <Panel title={<span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {dir === "entry" ? "ENTRY" : "EXIT"} LANE</span>} testid={`${dir}-panel`}
      action={<Sel value={laneId} onChange={setLaneId} testid={`${dir}-lane-select`} options={my.map((l) => ({ value: l.id, label: l.name }))} className="w-36" />}>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-1.5">
            {METHODS.filter(([m]) => dir === "entry" || m !== "rfid" || true).map(([m, lb, I]) => (
              <button key={m} data-testid={`${dir}-method-${m}`} onClick={() => setMethod(m)}
                className={`flex flex-col items-center gap-1 rounded-md border py-2 text-xs font-semibold transition-colors ${method === m ? "border-amber-500 bg-amber-500/10 text-amber-500" : "border-border hover:bg-muted"}`}>
                <I className="h-4 w-4" />{lb}
              </button>
            ))}
          </div>
          {(method === "lpr" || method === "manual" || (dir === "exit" && method === "qr" && false)) && (
            <Field label={method === "lpr" ? "LPR camera read (simulated)" : "Plate number"}>
              <div className="flex gap-2">
                <Input data-testid={`${dir}-plate`} className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} placeholder="ABC 1234" />
                {method === "lpr" && dir === "entry" && <Button variant="outline" size="icon" data-testid={`${dir}-lpr-capture`} onClick={() => setF({ ...f, plate: randPlate() })}><Camera className="h-4 w-4" /></Button>}
              </div>
            </Field>
          )}
          {method === "qr" && (
            <Field label={dir === "entry" ? "QR code (booking / pass)" : "Ticket QR code"}>
              <Input data-testid={`${dir}-qr`} className="font-mono uppercase" value={dir === "entry" ? f.qr_code : f.ticket_code}
                onChange={(e) => setF(dir === "entry" ? { ...f, qr_code: e.target.value } : { ...f, ticket_code: e.target.value.toUpperCase() })} placeholder={dir === "entry" ? "QR-PASS-…" : "e.g. 1A2B3C4D"} />
            </Field>
          )}
          {method === "qr" && dir === "entry" && (
            <Field label="Plate"><Input data-testid="entry-qr-plate" className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
          )}
          {method === "rfid" && (
            <Field label="RFID card no."><Input data-testid={`${dir}-rfid`} className="font-mono" value={f.rfid} onChange={(e) => setF({ ...f, rfid: e.target.value.toUpperCase() })} placeholder="RFID-0001" /></Field>
          )}
          {dir === "entry" && (
            <Field label="Vehicle type"><Sel value={f.vehicle_type} onChange={(v) => setF({ ...f, vehicle_type: v })} testid="entry-vehicle-type" className="w-full"
              options={[{ value: "car", label: "Car / SUV" }, { value: "motorcycle", label: "Motorcycle" }, { value: "van", label: "Van" }, { value: "truck", label: "Light truck" }]} /></Field>
          )}
          {dir === "exit" && (
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.lost_ticket} onCheckedChange={(v) => setF({ ...f, lost_ticket: !!v })} data-testid="exit-lost-ticket" /> Lost ticket (apply penalty)</label>
          )}
          <Button data-testid={`${dir}-submit`} disabled={busy || !laneId} onClick={submit} className={`w-full font-bold ${dir === "entry" ? "bg-cyan-600 hover:bg-cyan-500" : "bg-amber-500 text-slate-950 hover:bg-amber-400"}`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {dir === "entry" ? "Process entry" : "Process exit"}
          </Button>
        </div>
        <Result r={r} onPay={() => setPay(true)} onOverride={() => setOvr(true)} />
      </div>
      {r?.session && r.fee && <PayDialog session={r.session} fee={r.fee} open={pay} onOpenChange={setPay} onPaid={recheck} />}
      {can("override") && lane && <OverrideDialog open={ovr} onOpenChange={setOvr} barrierId={lane.barrier_id} plate={r?.session?.plate || f.plate} sessionId={r?.session?.id} onDone={() => setR(null)} />}
    </Panel>
  );
}

export default function Lanes() {
  const { branch, branches, setBranch } = useAuth();
  const [lanes, setLanes] = useState([]);
  const [forced, setForced] = useState(isForcedOffline());
  const bid = branch || branches[0]?.id || "";
  useEffect(() => {
    if (!bid) return;
    Promise.all([api.get(`/branches/${bid}`)]).then(([b]) => {
      const barriers = b.data.devices.filter((d) => d.type === "barrier");
      setLanes(b.data.lanes.map((l) => ({ ...l, barrier_id: barriers.find((x) => x.lane_id === l.id)?.id })));
    }).catch((e) => toast.error(fmtErr(e)));
  }, [bid]);
  const name = useMemo(() => branches.find((b) => b.id === bid)?.name, [branches, bid]);
  return (
    <div className="fade-up">
      <PageHeader title="Lane Console" subtitle={`${name || ""} — scan / identify → enter → pay → exit. Hardware is SIMULATED via modular adapters.`} testid="lanes-header">
        <BranchSel value={bid} onChange={setBranch} all={false} testid="lanes-branch" />
        <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold">
          <WifiOff className="h-3.5 w-3.5" /> Simulate offline
          <Switch data-testid="simulate-offline-toggle" checked={forced} onCheckedChange={(v) => { setForcedOffline(v); setForced(v); }} />
        </label>
      </PageHeader>
      <div className="grid gap-6 2xl:grid-cols-2">
        <LanePanel dir="entry" branchId={bid} lanes={lanes} />
        <LanePanel dir="exit" branchId={bid} lanes={lanes} />
      </div>
    </div>
  );
}
